// The rule editor on the "[New] Stateful agents" tab: the operator's playbook
// as an ordered set of individually addressable rules rather than one text box.
// See docs/Y2026-Q3/design-rule-based-operator-prompts.md.
//
// Every write appends one revision through the store, so the rules and the text
// the agent receives can never disagree.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  type AgentPromptDetail,
  type PromptRule,
  type RuleStatus,
  useDeleteRule,
  useReorderRules,
  useSavePreamble,
  useSaveRule,
} from "../api/hooks/useStatefulAgents";
import styles from "./StatefulAgentsRoute.module.css";

// Ordered as they render into the prompt, and split by whether the agent ever
// sees them. Which side a field falls on changes what you write in it, so the
// editor says it per field rather than expecting anyone to remember.
interface FieldSpec {
  key: keyof PromptRule;
  label: string;
  hint: string;
  rows: number;
  sent: boolean;
}

const FIELDS: FieldSpec[] = [
  {
    key: "guidance",
    label: "Instruction",
    hint: "What the agent should DO. Required.",
    rows: 6,
    sent: true,
  },
  {
    key: "why",
    label: "Why",
    hint: "The generalisation behind the rule. It is how the agent judges a case the instruction does not literally cover.",
    rows: 3,
    sent: true,
  },
  {
    key: "limits",
    label: "Limits",
    hint: "Where it stops applying, or how it gets over-applied. This is what stops the agent carrying one site's answer to another.",
    rows: 3,
    sent: true,
  },
  {
    key: "evidence",
    label: "Evidence",
    hint: "The cases behind it - project, counterparty, date, outcome. The agent uses these to calibrate how hard to push, and to cite our own precedent.",
    rows: 3,
    sent: true,
  },
  {
    key: "improvement",
    label: "How to do it better",
    hint: "A backlog of practice you have not adopted yet. Deliberately withheld - an agent told about it would act on it.",
    rows: 3,
    sent: false,
  },
];

// The sent fields first, then the operator-only ones: the divider below is
// placed at the boundary, so declaration order must not be able to contradict
// the per-field badge.
const ORDERED_FIELDS: FieldSpec[] = [
  ...FIELDS.filter((field) => field.sent),
  ...FIELDS.filter((field) => !field.sent),
];

const SENT_BADGE = "Sent to the agent";
const OPERATOR_BADGE = "Operator only";

const STATUS_LABEL: Record<RuleStatus, string> = {
  active: "In the prompt",
  draft: "Draft - not in the prompt",
  retired: "Retired",
};

type Draft = Partial<PromptRule>;

const NEW_RULE = "__new__";

const emptyDraft = (category: string): Draft => ({
  category,
  title: "",
  guidance: "",
  why: "",
  limits: "",
  evidence: "",
  improvement: "",
  attributes: {},
  status: "active",
});

// A conflict means someone else saved while this tab was open; the store refuses
// the write, so the operator must reload and re-apply.
export const errorText = (
  error: Error | null,
  conflict = "These instructions changed while you were editing. Reload the page and re-apply your change.",
): string | null => {
  if (!error) return null;
  const status = (error as { status?: number }).status;
  if (status === 409) {
    return conflict;
  }
  return error.message;
};

export const PromptRulesEditor = ({
  detail,
  actorEmail,
}: {
  detail: AgentPromptDetail;
  actorEmail: string;
}) => {
  const agent = detail.agent;
  const attributeSpecs = detail.rule_attributes;
  const baseRevision = detail.active.revision;
  const addendum = detail.active.addendum;

  const saveRule = useSaveRule(agent);
  const deleteRule = useDeleteRule(agent);
  const reorder = useReorderRules(agent);
  const savePreamble = useSavePreamble(agent);

  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [preamble, setPreamble] = useState<string>(addendum.preamble);
  const [storedPreamble, setStoredPreamble] = useState<string>(addendum.preamble);
  const [showRetired, setShowRetired] = useState(false);

  // A citation elsewhere links here as `...#R13`. The browser only resolves a
  // hash on a full page load, so a client-side navigation has to scroll itself,
  // and only once the rules have rendered - hence the dependency on the rule
  // count rather than on mount.
  const { hash } = useLocation();
  const ruleCount = addendum.rules.length;
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    target?.scrollIntoView({ block: "center" });
  }, [hash, ruleCount]);

  // Re-seed only when the STORED preamble moves, not on every revision bump, or a
  // rule edit would discard framing the operator is typing.
  if (storedPreamble !== addendum.preamble) {
    setStoredPreamble(addendum.preamble);
    setPreamble(addendum.preamble);
  }

  const ordered = [...addendum.rules].sort((a, b) => a.position - b.position);
  const live = ordered.filter((r) => r.status !== "retired");
  const retired = ordered.filter((r) => r.status === "retired");
  const activeCount = ordered.filter((r) => r.status === "active").length;
  const categories = Array.from(new Set(live.map((r) => r.category || "General")));

  const mutationError =
    errorText(saveRule.error) ??
    errorText(deleteRule.error) ??
    errorText(reorder.error) ??
    errorText(savePreamble.error);

  const startEdit = (rule: PromptRule) => {
    setEditing(rule.rule_id);
    setDraft({ ...rule });
  };

  const commit = () => {
    if (!draft.guidance?.trim()) return;
    saveRule.mutate(
      {
        base_revision: baseRevision,
        rule_id: editing === NEW_RULE ? undefined : (editing ?? undefined),
        rule: {
          category: draft.category ?? "",
          title: draft.title ?? "",
          guidance: draft.guidance ?? "",
          why: draft.why ?? "",
          limits: draft.limits ?? "",
          evidence: draft.evidence ?? "",
          improvement: draft.improvement ?? "",
          ...(attributeSpecs.length > 0 && { attributes: draft.attributes ?? {} }),
          status: draft.status,
        },
        actor_email: actorEmail,
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  // Scoped to the rule's own category, because that is the list the operator is
  // looking at: a flat move across a category boundary changes the stored order
  // without changing anything they can see.
  const move = (rule: PromptRule, delta: number) => {
    const siblings = live.filter((r) => (r.category || "General") === (rule.category || "General"));
    const from = siblings.findIndex((r) => r.rule_id === rule.rule_id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= siblings.length) return;
    const reordered = [...siblings];
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    const byId = new Map(reordered.map((r, i) => [r.rule_id, siblings[i].rule_id]));
    const ids = live.map((r) => byId.get(r.rule_id) ?? r.rule_id);
    reorder.mutate({
      base_revision: baseRevision,
      rule_ids: [...ids, ...retired.map((r) => r.rule_id)],
      actor_email: actorEmail,
    });
  };

  const setStatus = (rule: PromptRule, status: RuleStatus) =>
    saveRule.mutate({
      base_revision: baseRevision,
      rule_id: rule.rule_id,
      rule: { status },
      actor_email: actorEmail,
    });

  const renderEditor = () => (
    <div className={styles.ruleEditor}>
      <div className={styles.ruleEditorRow}>
        <label className={styles.ruleField}>
          Category <span className={styles.badgeSent}>{SENT_BADGE}</span>
          <input
            list={`${agent}-categories`}
            value={draft.category ?? ""}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
          />
          <datalist id={`${agent}-categories`}>
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
        <label className={styles.ruleField}>
          Title <span className={styles.badgeSent}>{SENT_BADGE}</span>
          <input
            value={draft.title ?? ""}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </label>
        <label className={styles.ruleField}>
          Status{" "}
          <span className={styles.badgeOperator}>Decides whether the agent sees the rule</span>
          <select
            value={draft.status ?? "active"}
            onChange={(e) => setDraft({ ...draft, status: e.target.value as RuleStatus })}
          >
            {(Object.keys(STATUS_LABEL) as RuleStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {attributeSpecs.length > 0 && (
        <div className={styles.ruleEditorRow}>
          {attributeSpecs.map((spec) => (
            <label key={spec.key} className={styles.ruleField}>
              {spec.label} <span className={styles.badgeSent}>{SENT_BADGE}</span>
              <input
                placeholder={spec.hint}
                title={spec.hint}
                value={draft.attributes?.[spec.key] ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    attributes: { ...draft.attributes, [spec.key]: e.target.value },
                  })
                }
              />
            </label>
          ))}
        </div>
      )}
      {/* Partitioned rather than walked in declaration order, so a field added
          to FIELDS in the wrong place cannot end up badged "Sent to the agent"
          below a line that says the agent never sees it. */}
      {ORDERED_FIELDS.map((field, index) => (
        <div key={field.key}>
          {!field.sent && ORDERED_FIELDS[index - 1]?.sent && (
            <p className={styles.fieldDivider}>
              Below this line is yours alone - the agent never sees it.
            </p>
          )}
          <label className={styles.ruleFieldBlock}>
            <span>
              {field.label}{" "}
              <span className={field.sent ? styles.badgeSent : styles.badgeOperator}>
                {field.sent ? SENT_BADGE : OPERATOR_BADGE}
              </span>{" "}
              <span className={styles.muted}>{field.hint}</span>
            </span>
            <textarea
              rows={field.rows}
              spellCheck={false}
              value={(draft[field.key] as string) ?? ""}
              onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
            />
          </label>
        </div>
      ))}
      <div className={styles.saveRow}>
        <button
          type="button"
          className={styles.primary}
          disabled={!draft.guidance?.trim() || saveRule.isLoading}
          onClick={commit}
        >
          {saveRule.isLoading ? "Saving…" : "Save as new revision"}
        </button>
        <button type="button" className={styles.link} onClick={() => setEditing(null)}>
          Cancel
        </button>
        {!draft.guidance?.trim() && (
          <span className={styles.muted}>An instruction is required</span>
        )}
      </div>
    </div>
  );

  // `id` is the anchor a citation links to: an action on the Grid tab carries
  // the rule ids it came from, and `#R13` has to land on the rule itself.
  const renderRule = (rule: PromptRule) => (
    <div key={rule.rule_id} id={rule.rule_id} className={styles.ruleCard}>
      <div className={styles.ruleHeader}>
        <span className={styles.ruleId}>{rule.rule_id}</span>
        <span className={styles.ruleTitle}>{rule.title || rule.guidance.slice(0, 80)}</span>
        {rule.status !== "active" && (
          <span className={styles.ruleBadge}>{STATUS_LABEL[rule.status]}</span>
        )}
        {/* One edit at a time: every action here appends a revision, so a
            second write while a rule editor is open would rebase that editor's
            unsaved draft onto content the operator has not seen. */}
        <span className={styles.rowActions} hidden={editing !== null}>
          <button type="button" className={styles.link} onClick={() => startEdit(rule)}>
            Edit
          </button>
          {rule.status !== "retired" && (
            <>
              <button type="button" className={styles.link} onClick={() => move(rule, -1)}>
                Up
              </button>
              <button type="button" className={styles.link} onClick={() => move(rule, 1)}>
                Down
              </button>
            </>
          )}
          <button
            type="button"
            className={styles.link}
            onClick={() => setStatus(rule, rule.status === "active" ? "retired" : "active")}
          >
            {rule.status === "active" ? "Retire" : "Put in the prompt"}
          </button>
          <button
            type="button"
            className={styles.link}
            onClick={() => {
              // Recoverable: earlier revisions still carry the rule and ids are never re-minted.
              if (!window.confirm(`Delete ${rule.rule_id}? You can restore an earlier revision.`))
                return;
              deleteRule.mutate({
                base_revision: baseRevision,
                rule_id: rule.rule_id,
                actor_email: actorEmail,
              });
            }}
          >
            Delete
          </button>
        </span>
      </div>
      {editing === rule.rule_id ? (
        renderEditor()
      ) : (
        <>
          {attributeSpecs
            .filter((spec) => rule.attributes?.[spec.key])
            .map((spec) => (
              <p key={spec.key} className={styles.muted}>
                {spec.label}: {rule.attributes[spec.key]}
              </p>
            ))}
          <p className={styles.ruleGuidance}>{rule.guidance}</p>
        </>
      )}
    </div>
  );

  return (
    <>
      <h2 className={styles.sectionTitle}>
        Framing <span className={styles.badgeSent}>{SENT_BADGE}</span>
      </h2>
      <p className={styles.muted}>
        Role and domain, in your words. Everything else is a rule.
      </p>
      <textarea
        className={styles.editor}
        rows={5}
        spellCheck={false}
        value={preamble}
        onChange={(e) => setPreamble(e.target.value)}
      />
      <div className={styles.saveRow}>
        <button
          type="button"
          className={styles.primary}
          disabled={preamble === addendum.preamble || savePreamble.isLoading}
          onClick={() =>
            savePreamble.mutate({
              base_revision: baseRevision,
              preamble,
              actor_email: actorEmail,
            })
          }
        >
          {savePreamble.isLoading ? "Saving…" : "Save framing"}
        </button>
        {preamble !== addendum.preamble && (
          <span className={styles.muted}>Unsaved changes</span>
        )}
      </div>

      <h2 className={styles.sectionTitle}>
        Rules{" "}
        <span className={styles.muted}>
          {activeCount} in the prompt · {detail.active.prompt_text.length.toLocaleString()}{" "}
          characters (~{Math.round(detail.active.prompt_text.length / 4).toLocaleString()}{" "}
          tokens) in every turn
        </span>
      </h2>
      <p className={styles.muted}>
        A rule&apos;s category, title, instruction, why, limits and evidence all go to the
        agent. &ldquo;How to do it better&rdquo; never does - it is a backlog of practice
        you have not adopted, and an agent told about it would act on it. Only rules that
        are <strong>in the prompt</strong> are sent at all; drafts and retired rules stay
        here. Open the preview below to read exactly what the agent receives.
      </p>
      {mutationError && <p className={styles.error}>{mutationError}</p>}

      {categories.map((category) => (
        <section key={category}>
          <h3 className={styles.ruleCategory}>{category}</h3>
          {live.filter((r) => (r.category || "General") === category).map(renderRule)}
        </section>
      ))}

      {editing === NEW_RULE ? (
        <div className={styles.ruleCard}>
          <div className={styles.ruleHeader}>
            <span className={styles.ruleId}>New rule</span>
          </div>
          {renderEditor()}
        </div>
      ) : (
        <button
          type="button"
          className={styles.primary}
          disabled={editing !== null}
          onClick={() => {
            setEditing(NEW_RULE);
            setDraft(emptyDraft(categories[0] ?? ""));
          }}
        >
          Add a rule
        </button>
      )}

      {retired.length > 0 && (
        <details
          className={styles.scaffold}
          open={showRetired}
          onToggle={(e) => setShowRetired((e.target as HTMLDetailsElement).open)}
        >
          <summary>Retired rules ({retired.length}) - kept, not sent to the agent</summary>
          {retired.map(renderRule)}
        </details>
      )}
    </>
  );
};
